
import { Component, Model, Prop, Vue, Watch } from "vue-property-decorator";
import vSelect3 from "vselect3";
import { validationMixin } from "vuelidate";
import { email, maxLength, requiredIf } from "vuelidate/lib/validators";
import { filter, debounce } from "lodash";
import api from "@/services/api.service";
import { Getter, namespace } from "vuex-class";
import { UserPermissions, PaginatedSearchQueryParams } from "@/store/types";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import SearchDropDown from "@/components/form/SearchDropDown.vue";
import notificationTemplatesApi from "@/services/api.notificationTemplate.service";
import NotificationDocumentTypeId from "@/types/sv-data/enums/NotificationDocumentTypeIds";
import NotificationTemplateTypeId from "@/types/sv-data/enums/NotificationTemplateTypeIds";
import SetEventOutcomeAndNote from "@/components/site-monitor/SetEventOutcomeAndNote.vue";
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import { NotificationTemplateManualField } from "@/types/sv-data/notification-templates/NotificationTemplateManualField";
import NotificationTemplateCreateRequest from "@/types/sv-data/notification-templates/NotificationTemplateCreateRequest";
import axios, { CancelTokenSource } from "axios";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import { getDebouncePeriod } from '@/utils/debounce-helper';
import CustomDefinedField from "@/components/custom-fields/CustomDefinedField.vue";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldValues } from "@/store/custom-fields/types";
import { hasEmptyRequiredCustomFields, sortOrderByNullLast, hasInvalidCustomFieldValues } from "@/utils/CustomFieldLogic";
import NotificationTemplateFormatOption, { notificationTemplateFormats } from "@/types/sv-data/notification-templates/NotificationTemplateFormatOption";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";
import NotificationRuleAction from "@/types/sv-data/notification-rule/NotificationRuleAction";
import NotificationRuleActionContactMembership from "@/types/sv-data/notification-rule/NotificationRuleActionContactMembership";
import ContactEmailSelect from "@/components/contacts/ContactEmailSelect.vue";
import { EventOutcome } from "@/types/EventOutcome";
import { NotifyNotificationRules } from "@/store/site-monitor/types";

const CustomFields = namespace("customFields");

interface ContactOption {
	email: string;
	fullName: string | null;
	userID?: number;
}

interface ContactGroupOption {
	id: string;
}

interface ManualFieldInput {
	field: NotificationTemplateManualField;
	value: string;
}

const MAX_NOTE_LENGTH = 5000;

@Component({
	components: {
		"v-select-3": vSelect3,
		"search-drop-down": SearchDropDown,
		"set-event-outcome-and-note": SetEventOutcomeAndNote,
		"custom-field": CustomDefinedField,
		"contact-email-select": ContactEmailSelect
	},
	mixins: [validationMixin],
	validations: {
		note: {
			maxLength: maxLength(MAX_NOTE_LENGTH)
		},
		addresses: {
			required: requiredIf(function () {
				return this.selectedContactGroups && this.selectedContactGroups.length < 1
			}),
			$each: {
				email: { email }
			}
		},
		selectedContactGroups: {
			required: requiredIf(function () {
				return this.addresses && this.addresses.length < 1
			}),
		}
	}
})
export default class NotificationModal extends Vue {
	@Prop({ type: Number }) readonly groupId: number;
	@Prop({ type: Number }) readonly eventId: number;
	@Model("change", { type: Boolean }) readonly isVisible: boolean;

	@Prop({type: Boolean, required: false, default: true})
	private notifyOpenedManually: boolean;

	@Prop({ type: Object, required: false})
	private notifyNotificationRules: NotifyNotificationRules;

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@CustomFields.State private notificationTemplateCustomFields: CustomFieldDto[];
	@CustomFields.Action retrieveCustomFields: ({ tableType, live, skipCancel }) => Promise<void>;

	private emails: ContactOption[] = [];
	private emailsLoaded = false;

	private addresses: ContactOption[] = [];
	private selectedContactGroups: ContactGroup[] = [];
	private note = "";
	private attachSitRep = true;
	private notificationTemplates: NotificationTemplate[] = [];
	private selectedNotificationTemplate: NotificationTemplate = null;
	private selectedTemplateFormat: NotificationTemplateFormatOption = null;
	private availableManualFields: ManualFieldInput[] = null;
	private availableCustomFields: ManualFieldInput[] = null;
	private orderedCustomFieldList: CustomFieldDto[] = [];
	private eventOutcome: EventOutcomeAndNote = {
		valid: true,
		eventOutcomeNote: null,
		eventOutcomeId: null,
		eventOutcomePath: []
	};

	private contactGroupSearchToken: CancelTokenSource = null;
	private contactGroups: ContactGroup[] = [];
	private notificationTemplateFormats: NotificationTemplateFormatOption[] = notificationTemplateFormats;

	private async mounted(): Promise<void> {
		await this.loadContacts();
		await this.loadNotificationTemplates();

		if (this.isContactGroupsEnabled) {
			await this.retrieveContactGroups();
		}

		if (this.isNotificationTemplatesCustomFieldsEnabled) {
			await this.retrieveCustomFields({ tableType: CustomFieldTableTypes.NotificationTemplate, live: true, skipCancel: false });
			this.orderedCustomFieldList = this.notificationTemplateCustomFields;
			sortOrderByNullLast(this.orderedCustomFieldList);
		}

		if (this.isNotificationRulesEnabled) {
			await this.onNotifyNotificationRulesChanged(this.notifyNotificationRules, null);
		}
	}

	private get isNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates"]);
	}

	private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

	private get isNotificationTemplatesCustomFieldsEnabled(): boolean {
		return this.getFeature(["CustomFields", "NotificationTemplates"]);
	}

	private get hasNotificationTemplatePermissions(): boolean {
		return this.permissions.canViewNotificationTemplates || this.permissions.canEditNotificationTemplates;
	}

	private get hasCanViewCustomFieldsPermission(): boolean {
		return this.permissions.canViewCustomFields;
	}

	private get eventNote(): string {
		return this.notifyNotificationRules?.eventNote ?? "";
	}

	private get eventOutcomePath(): EventOutcome[] {
		return this.notifyNotificationRules?.eventOutcomePath ?? [];
	}

	private get eventOutcomeId(): number {
		return this.notifyNotificationRules?.eventOutcomePath[this.notifyNotificationRules.eventOutcomePath.length - 1]?.eventOutcomeID;
	}

	private get isNotificationRulesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationRules"]);
	}

	private get hasNotificationRulePermissions(): boolean {
		return this.permissions.canViewNotificationRules || this.permissions.canEditNotificationRules;
	}

	private get canUseNotificationRules(): boolean {
		return this.isNotificationRulesEnabled && this.hasNotificationRulePermissions
	}

	@Watch("notifyNotificationRules")
	private async onNotifyNotificationRulesChanged(newRules: NotifyNotificationRules, oldRules: NotifyNotificationRules): Promise<void> {
		if (oldRules) {
			// Ensure Contacts / Groups added by Notification Rules cleared when Rules change
			this.clearNotificationRules(oldRules.rules);
		}

		if (newRules) {
			this.applyNotificationRules();
		}
	}

	@Watch("groupId")
	private async loadNotificationTemplates(): Promise<void> {
		if (!this.isNotificationTemplatesEnabled || !this.hasNotificationTemplatePermissions) {
			return;
		}

		try
		{
			// Default attaching the sitrep to false if we have notification templates enabled
			this.attachSitRep = false;
			this.notificationTemplates = await notificationTemplatesApi.getAllNotificationTemplates(NotificationTemplateTypeId.Event, this.groupId);
		} catch (ex) {
			console.error("Unexpected error fetching notification templates: " + ex);
		}
	}

	private get userIds(): number[] {
		return this.addresses.filter((contact: ContactOption) =>
				contact.userID !== null
			).map((contact: ContactOption) => contact.userID);
	}

	private async getContacts(): Promise<void> {
		try {
			this.emails = await api.getContactsPaginated({
				params: {
					page: 1,
					pageSize: 100,
					searchTerm: "",
					requiredUserIds: this.userIds
				},
				cancelToken: null
			});
		} catch (error) {
			console.error("Error when getting contacts", error)
		}
	}

	@Watch("groupId")
	private async loadContacts() {
		this.emailsLoaded = false;
		await this.getContacts();
		this.emailsLoaded = true;
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
			await this.retrieveContactGroups(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true });

	@Watch("groupId")
	private async retrieveContactGroups(searchTerm: string = ""): Promise<void> {
		let params: PaginatedSearchQueryParams = {
			page: 1,
			pageSize: 50,
			sortBy: "title",
			searchTerm: searchTerm
		}

		this.contactGroupSearchToken = axios.CancelToken.source();

		let request: PaginatedSearchRequest = {
			params: params,
			cancelToken: this.contactGroupSearchToken.token
		}

		try {
			this.contactGroups = (await api.getContactGroups(request)).data;
		}
		catch (err) {
			console.error(err);
			this.$notify({
				type: "error",
				title: "Retrieving Contact Groups",
				text: err.message
			});
			return;
		}
	}

	private createOption(newOption: string): ContactOption {
		const newContact = {
			email: newOption,
			fullName: null
		};

		this.$emit("option:created", newContact);
		return newContact;
	}

	private createContactGroupOption(newOption: string): ContactGroupOption {
		const newContactGroup = {
			id: newOption,
		};

		this.$emit("option:created", newContactGroup);
		return newContactGroup;
	}

	private getOptionLabel(option: ContactOption) {
		if (option.fullName) {
			return option.fullName;
		}
		return option.email;
	}

	private getContactGroupOptionLabel(option: ContactGroupOption): string {
		return option.id;
	}

	private get noteInvalidFeedback(): string {
		if (!this.$v.note.maxLength) {
			return `Note must have no more than ${this.$v.note.$params.maxLength.max} letters.`;
		}
		return "";
	}

	private get addressesInvalidFeedback(): string {
		const { required, $each: each } = this.$v.addresses;
		if (!required) {
			let feedbackString = "Please specify at least one recipient";

			if (this.isContactGroupsEnabled) {
				feedbackString = feedbackString.concat(" (or contact group)");
			}

			return feedbackString;
		}

		const invalidEmails = filter(each.$iter, v => !v.email.email).map(v => v.email.$model);
		if (invalidEmails.length > 0) {
			const adrWord = invalidEmails.length === 1 ? "address" : "addresses";
			const addresses = invalidEmails.map(a => `"${a}"`).join(", ");
			return `The ${adrWord} ${addresses} in the "Email Address" field was not recognized. Please make sure that all addresses are properly formed.`;
		}

		return "";
	}

	private get contactGroupsInvalidFeedback(): string {
		const { required } = this.$v.selectedContactGroups;

		if (!required) {
			return "Please specify at least one contact group (or contact)";
		}

		return "";
	}

	private get noteValidFeedback() {
		const charactersUsed = this.note !== null ? this.note.length : 0;
		return `Characters used: ${charactersUsed}/${MAX_NOTE_LENGTH}`;
	}

	private get notifyRules(): NotificationRule[] {
		return this.notifyNotificationRules?.rules ?? null;
	}

	private onHide(event: { trigger: string }) : void {
		const cancelTrigger = "cancel";
		this.addresses = [];
		this.selectedContactGroups = [];
		this.note = "";
		this.selectedNotificationTemplate = null;
		this.selectedTemplateFormat = null;

		if (event.trigger === cancelTrigger) {
			this.$emit("cancel");
		}
		this.$v.$reset();
	}

	private async onSend() {
		let createNotificationDocument: NotificationTemplateCreateRequest = null;
		if (this.selectedNotificationTemplate != null) {
			createNotificationDocument = {
				notificationTemplateId: this.selectedNotificationTemplate.notificationTemplateId,
				notificationDocumentTypeId: this.selectedTemplateFormat.id,
				note: this.note,
				manualInputFields: this.availableManualFields.reduce((map, obj) => {
					map[obj.field.id] = obj.value;
					return map;
				}, {}),
				customFieldInputs: this.availableCustomFields.reduce((map, obj) => {
					map[obj.field.id] = obj.value;
					return map;
				}, {})
			};
		}

		const payload = {
			contacts: [...this.addresses],
			contactGroupIds: this.selectedContactGroups.map(cg => cg.contactGroupId),
			note: this.note,
			attachSitRep: this.attachSitRep,
			createNotificationDocumentRequest: createNotificationDocument
		};

		if (this.eventOutcome.eventOutcomeId != null || (this.eventOutcome.eventOutcomeNote != null && this.eventOutcome.eventOutcomeNote.length > 0)) {
			const eventOutcomeAndNoteForm = this.$refs.eventOutcomeAndNote as any;
			try {
				await eventOutcomeAndNoteForm.submit();
			}
			catch(err) {
				console.error(err);
				this.$notify({
					type: "error",
					title: "Updating Event Outcome",
					text: err.message
				});

				// Break out of function so notification doesn't get sent
				return;
			}
		}

		if (this.canUseNotificationRules) {
			this.$emit("notifySent", this.groupId, this.eventOutcomeId, this.eventNote);
		}
		await api.sendEventNotification(this.eventId, payload);
	}

	private onModalVisibilityChange(isVisible: boolean) {
		this.$emit("change", isVisible);
	}

	private async onPreviewTemplate(): Promise<void> {

		let override = {};
		if (this.eventOutcome?.eventOutcomeNote != null && this.eventOutcome?.eventOutcomeNote.length > 0) {
			override["eventOutcomeNote"] = this.eventOutcome.eventOutcomeNote;
		}

		// Hard coded to PDF as XSS middleware doesn't like HTML file responses
		// and this will need to be investigated
		await notificationTemplatesApi.previewNotificationTemplate(this.eventId, {
			notificationTemplate: this.selectedNotificationTemplate,
			notificationDocumentTypeId: NotificationDocumentTypeId.Pdf,
			eventOutcomeOverride: this.eventOutcome?.eventOutcomeId,
			placeholderOverrides: override,
			note: this.note,
			manualInputFields: this.availableManualFields != null ? this.availableManualFields.reduce((map, obj) => {
				map[obj.field.id] = obj.value;
				return map;
			}, {}) : [],
			customFieldInputs: this.availableCustomFields != null ? this.availableCustomFields.reduce((map, obj) => {
				map[obj.field.id] = obj.value;
				return map;
			}, {}) : []
		});
	}

	@Watch("selectedNotificationTemplate")
	private updateAvailableManualFields(): void {
		this.availableManualFields = [];
		this.availableCustomFields = [];
		if (this.selectedNotificationTemplate == null) {
			this.selectedTemplateFormat = null;
			return;
		}

		if (this.selectedTemplateFormat === null) {
			this.setDefaultTemplateFormat();
		}

		let manualFields = this.selectedNotificationTemplate.manualFields;
		if (manualFields != null) {
			manualFields.forEach((element) => {
				this.availableManualFields.push({
					field: element,
					value: ""
				});
			});
		}

		let customFields = this.selectedNotificationTemplate.customFields;
		if (customFields != null) {
			customFields.forEach((element) => {
				this.availableCustomFields.push({
					field: element,
					value: ""
				});
			});
		}

		if (this.isContactGroupsEnabled && !!this.selectedNotificationTemplate.contactGroups) {
			this.selectedNotificationTemplate.contactGroups.forEach(contactGroup => {
				if (!this.selectedContactGroups.some(id => id.contactGroupId === contactGroup.contactGroupId)) {
					this.selectedContactGroups.push(contactGroup);
				}
			});
		}
	}

	private get canSendNotification(): boolean {
		if (this.$v.$invalid) {
			return false;
		}

		if (this.selectedNotificationTemplate != null) {
			if(!this.eventOutcome.valid) {
				return false;
			}

			if (this.canDisplayCustomFields) {
				let fieldsToCheck = this.availableCustomFields.map(cf => this.getCustomFieldSetting(cf.field))
				.filter(cf => !!cf);

				return !hasEmptyRequiredCustomFields(this.availableCustomFieldsToCustomFieldValues, fieldsToCheck)
					&& !hasInvalidCustomFieldValues(this.availableCustomFieldsToCustomFieldValues, fieldsToCheck);
			}
			return this.selectedTemplateFormat !== null;
		}

		if (this.notifyNotificationRules) {
			if (!this.selectedNotificationTemplate) {
				return false;
			}
		}

		return true;
	}

	private async eventOutcomeDetailsChanged(newValue: EventOutcomeAndNote): Promise<void> {
		if (this.canUseNotificationRules) {
			this.$emit("notifyRuleReload", this.eventId, newValue.eventOutcomePath, newValue.eventOutcomeNote);
		}
		this.eventOutcome = newValue;
	}

	private setDefaultTemplateFormat(): void {
		if (this.isNotificationTemplatesEnabled) {
			const defaultTemplateFormatLabel = "Embedded";
			const defaultTemplateFormatIndex = this.notificationTemplateFormats.findIndex(f => f.label === defaultTemplateFormatLabel);
			this.selectedTemplateFormat = this.notificationTemplateFormats[defaultTemplateFormatIndex];
		}
	}

	private onSearch(searchTerm: string): void{
		this.handleSearchInput(searchTerm);
	}

	private getCustomFieldSetting(customField: NotificationTemplateManualField): CustomFieldDto {
		let customFieldSetting = this.orderedCustomFieldList.find(cf => cf.id == customField.id);
		return customFieldSetting;
	}

	private getCustomFieldValue(customField: NotificationTemplateManualField): CustomFieldValues {
		let value: string = "";
		let existing: ManualFieldInput = this.availableCustomFields.firstOrDefault(cf => cf.field?.id == customField?.id);

		if (existing) {
			value = existing.value;
		}

		let customFieldValue = {
			id: customField.id,
			value: value
		}

		return customFieldValue;
	}

	private onCustomFieldValueChanged(updatedValue: CustomFieldValues): void {
		this.availableCustomFields.find(cf => cf.field.id === updatedValue.id).value = updatedValue.value;
	}

	private get availableCustomFieldsToCustomFieldValues(): CustomFieldValues[] {
		var customfieldValuesList: CustomFieldValues[] = [];

		for (let i = 0; i < this.availableCustomFields.length; i++) {
			let customFieldValue = {
				id: this.availableCustomFields[i].field.id,
				value: this.availableCustomFields[i].value
			}

			customfieldValuesList.push(customFieldValue);
		}
		return customfieldValuesList
	}

	private get canDisplayCustomFields(): boolean {
		return this.selectedNotificationTemplate != null
		&& this.isNotificationTemplatesCustomFieldsEnabled
		&& this.hasCanViewCustomFieldsPermission
		&& this.selectedNotificationTemplate.customFields
		&& this.selectedNotificationTemplate.customFields.length > 0;
	}

	private sortCustomFieldsByOrder(customFields: NotificationTemplateManualField[]): NotificationTemplateManualField[] {
		var orderedCustomFields: NotificationTemplateManualField[] = [];

		this.orderedCustomFieldList.forEach(ocf => {
			var matchedCustomField = customFields.find(cf => cf.id == ocf.id);

			if (matchedCustomField) {
				orderedCustomFields.push(matchedCustomField);
			}
		})

		return orderedCustomFields;
	}

	private getEventOutcomeId(eventOutcomePath: EventOutcome[]): number {
		return eventOutcomePath[eventOutcomePath.length - 1]?.eventOutcomeID;
	}

	private applyNotificationRules(): void {
		if (this.notifyNotificationRules && this.notifyNotificationRules.rules) {
			this.notifyNotificationRules.rules.forEach((rule :NotificationRule) => {
				rule.actions.forEach((action: NotificationRuleAction) => {
					action.sendTo.forEach((sendTo: NotificationRuleActionContactMembership) => {

						const sendToUser = !!sendTo.userId;
						if (sendToUser) {
							const user = this.emails.firstOrDefault((e: ContactOption) => e.userID === sendTo.userId);
							if (!user) {
								return;
							}

							const userExists = this.addresses.firstOrDefault((c: ContactOption) => c.userID === user.userID);
							if (!userExists) {
								Vue.set(this.addresses, this.addresses.length, user);
							}
							return;
						}

						const sendToEmail = !sendTo.userId && sendTo.emailAddress;
						if (sendToEmail) {
							const existingEmail = this.addresses.firstOrDefault((c: ContactOption) => !c.userID && c.email == sendTo.emailAddress);
							if (!existingEmail) {
								Vue.set(this.addresses, this.addresses.length, {
										email: sendTo.emailAddress,
										fullName: "",
										userID: null
								});
							}
							return;
						}

						const sendToContactGroup = !sendTo.userId && !sendTo.emailAddress && sendTo.contactGroupId;
						if (sendToContactGroup) {
							const contactGroup = this.contactGroups.firstOrDefault((c: ContactGroup) => c.contactGroupId == sendTo.contactGroupId);
							if (!contactGroup) {
								return;
							}

							const existingContactGroup = this.selectedContactGroups.firstOrDefault((c: ContactGroup) => c.contactGroupId === sendTo.contactGroupId);
							if (!existingContactGroup) {
								Vue.set(this.selectedContactGroups, this.selectedContactGroups.length, {
									contactGroupId: contactGroup.contactGroupId,
									title: contactGroup.title,
									suiteTenantId: contactGroup.suiteTenantId,
									groupId: contactGroup.groupId
								});
							}
						}
					})
				});
			});
		}
	}

	private clearNotificationRules(notificationRules: NotificationRule[]): void {
		if (notificationRules && notificationRules) {
			notificationRules.forEach((rule :NotificationRule) => {
				rule.actions.forEach((action: NotificationRuleAction) => {
					action.sendTo.forEach((sendTo: NotificationRuleActionContactMembership) => {

						const sendToUser = !!sendTo.userId;
						if (sendToUser) {
							const user = this.emails.firstOrDefault((e: ContactOption) => e.userID === sendTo.userId);

							const userExists = this.addresses.firstOrDefault((c: ContactOption) => c.userID === user.userID);
							if (userExists) {
								this.addresses.remove(userExists);
							}
							return;
						}

						const sendToEmail = !sendTo.userId && sendTo.emailAddress;
						if (sendToEmail) {
							const existingEmail = this.addresses.firstOrDefault((c: ContactOption) => !c.userID && c.email == sendTo.emailAddress);
							if (existingEmail) {
								this.addresses.remove(existingEmail);
							}
							return;
						}

						const sendToContactGroup = !sendTo.userId && !sendTo.emailAddress && sendTo.contactGroupId;
						if (sendToContactGroup) {
							const existingContactGroup = this.selectedContactGroups.firstOrDefault((c: ContactGroup) => c.contactGroupId === sendTo.contactGroupId);
							if (existingContactGroup) {
								this.selectedContactGroups.remove(existingContactGroup);
							}
						}
					})
				});
			});
		}
	}

	private skipNotify(): void {
		this.$emit("notifySkipped", this.groupId, this.eventOutcomeId, this.eventNote);
	}

	private get dontCloseOnBackDrop(): boolean {
		const enforceDismissingByButtons = this.getFeature(["TenantSettings", "EnforceDismissingModalByButtons"]);
  		return enforceDismissingByButtons || !this.notifyOpenedManually;
	}
}
